import type { MapViewState } from "../map/presets";
import { useT } from "../hooks/useLang";

interface MapOverlayControlsProps {
  mapViewState: MapViewState;
  onAerialOnly: () => void;
  onClearOverlays: () => void;
  onRestoreLens: () => void;
}

// Known custom-view labels (set in App.tsx) → Thai. Falls back to the raw
// label for anything not in the map (e.g. metric-driven custom views).
const CUSTOM_LABEL_TH: Record<string, string> = {
  "Clean aerial view": "มุมมองภาพถ่าย",
  "Overlays hidden": "ซ่อนชั้นข้อมูล",
};

export function MapOverlayControls({
  mapViewState,
  onAerialOnly,
  onClearOverlays,
  onRestoreLens,
}: MapOverlayControlsProps) {
  const t = useT();
  const isCustom = mapViewState.kind === "custom";
  const rawLabel = isCustom ? (mapViewState as { kind: "custom"; label: string }).label : null;
  const label = rawLabel ? t(rawLabel, CUSTOM_LABEL_TH[rawLabel] ?? rawLabel) : null;

  return (
    <div className="map-overlay-controls">
      {isCustom && label && (
        <span className="map-view-status" role="status" aria-live="polite">
          <span className="map-view-status-dot" />
          {label}
        </span>
      )}
      <div className="map-overlay-actions">
        {isCustom ? (
          <button
            onClick={onRestoreLens}
            aria-label="Restore default layers for the current lens"
            title="Restore lens"
          >
            {t("RESTORE LENS", "คืนค่ามุมมอง")}
          </button>
        ) : (
          <>
            <button
              onClick={onAerialOnly}
              aria-label="Switch to aerial-only view — disables all data overlays"
              title="Aerial only"
            >
              {t("AERIAL", "ภาพถ่าย")}
            </button>
            <button
              onClick={onClearOverlays}
              aria-label="Clear all active map overlays"
              title="Clear overlays"
            >
              {t("CLEAR", "ล้าง")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
